import type { AdaptiveFormProps, FieldInputProps } from "@kotaio/adaptive-form/react";

import { useEffect } from "react";

import {
  Checkbox,
  cn,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Typography,
} from "~/ui";

type FieldComputedProps = Parameters<
  NonNullable<NonNullable<AdaptiveFormProps["components"]>["computed"]>
>[0];

function getRequiredClassName(isRequired?: boolean) {
  return cn(isRequired && 'after:ml-0.5 after:text-destructive after:content-["*"]');
}

function FieldError({ fieldId, errors }: { fieldId: string; errors: string[] }) {
  if (errors.length === 0) return null;

  return (
    <p id={`${fieldId}-errors`} className="text-xs text-destructive" role="alert">
      {errors.join(", ")}
    </p>
  );
}

function FieldWrapper({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("flex flex-col gap-2 py-3", className)}>{children}</div>;
}

function FieldLabel({
  htmlFor,
  label,
  isRequired,
  className,
}: {
  htmlFor?: string;
  label?: React.ReactNode;
  isRequired?: boolean;
  className?: string;
}) {
  if (label == null) return null;

  return (
    <Label
      htmlFor={htmlFor}
      className={cn(
        "text-sm font-medium text-foreground",
        getRequiredClassName(isRequired),
        className,
      )}
    >
      {label}
    </Label>
  );
}

function PlaygroundTextInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const stringValue = value == null ? "" : String(value);
  const placeholder = typeof field.placeholder === "string" ? field.placeholder : undefined;

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={field.id} label={label} isRequired={isRequired} />
      <Input
        id={field.id}
        type="text"
        value={stringValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        aria-invalid={errors.length > 0}
        aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
        className={cn(errors.length > 0 && "border-destructive")}
      />
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundNumberInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const numValue = value == null ? "" : Number(value);
  const stringValue = numValue === "" ? "" : String(numValue);
  const placeholder = typeof field.placeholder === "string" ? field.placeholder : undefined;

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={field.id} label={label} isRequired={isRequired} />
      <Input
        id={field.id}
        type="number"
        value={stringValue}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? undefined : Number(v));
        }}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        aria-invalid={errors.length > 0}
        aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
        className={cn(errors.length > 0 && "border-destructive")}
      />
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundEmailInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const stringValue = value == null ? "" : String(value);
  const placeholder = typeof field.placeholder === "string" ? field.placeholder : undefined;

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={field.id} label={label} isRequired={isRequired} />
      <Input
        id={field.id}
        type="email"
        value={stringValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        aria-invalid={errors.length > 0}
        aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
        className={cn(errors.length > 0 && "border-destructive")}
      />
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundDateInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  let stringValue = "";
  if (value != null) {
    if (typeof value === "string") stringValue = value.slice(0, 10);
    else if (value instanceof Date) stringValue = value.toISOString().slice(0, 10);
  }

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={field.id} label={label} isRequired={isRequired} />
      <Input
        id={field.id}
        type="date"
        value={stringValue}
        onChange={(e) => onChange(e.target.value || undefined)}
        onBlur={onBlur}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        aria-invalid={errors.length > 0}
        aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
        className={cn(errors.length > 0 && "border-destructive")}
      />
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundRadioInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  options,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const currentValue = value == null ? "" : String(value);

  return (
    <FieldWrapper>
      <FieldLabel label={label} isRequired={isRequired} />
      <RadioGroup
        value={currentValue}
        onValueChange={(val) => {
          const selectedOption = options?.find((opt) => String(opt.value) === val);
          onChange(selectedOption?.value ?? val);
        }}
        onBlur={onBlur}
        disabled={isReadOnly}
        aria-invalid={errors.length > 0}
        aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
        className="flex flex-col gap-2.5"
      >
        {Array.isArray(options) &&
          options.map((opt) => {
            const id = `${field.id}-${String(opt.value)}`;
            return (
              <div key={String(opt.value)} className="flex items-center gap-2.5">
                <RadioGroupItem id={id} value={String(opt.value)} disabled={isReadOnly} />
                <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                  {opt.label}
                </Label>
              </div>
            );
          })}
      </RadioGroup>
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundSelectInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  options,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const stringValue = value == null ? "" : String(value);
  const placeholder = typeof field.placeholder === "string" ? field.placeholder : "Select...";

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={field.id} label={label} isRequired={isRequired} />
      <Select
        value={stringValue || undefined}
        onValueChange={(val) => {
          const selectedOption = options?.find((opt) => String(opt.value) === val);
          onChange(selectedOption?.value ?? val);
        }}
        disabled={isReadOnly}
      >
        <SelectTrigger
          id={field.id}
          onBlur={onBlur}
          aria-invalid={errors.length > 0}
          aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
          className={cn(errors.length > 0 && "border-destructive")}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {Array.isArray(options) &&
            options.map((opt) => (
              <SelectItem key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundCheckboxInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const checked = value === true;

  return (
    <FieldWrapper>
      <div className="flex items-start gap-2.5">
        <Checkbox
          id={field.id}
          checked={checked}
          onCheckedChange={(c) => onChange(c === true)}
          onBlur={onBlur}
          disabled={isReadOnly}
          aria-invalid={errors.length > 0}
          aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
          className="mt-0.5"
        />
        <div className="flex flex-col gap-1">
          <FieldLabel
            htmlFor={field.id}
            label={label}
            isRequired={isRequired}
            className={cn(
              "cursor-pointer font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
            )}
          />
        </div>
      </div>
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundToggleInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const checked = value === true;
  const fieldRecord = field as unknown as Record<string, unknown>;
  const description =
    "description" in field && typeof fieldRecord["description"] === "string"
      ? fieldRecord["description"]
      : undefined;

  return (
    <FieldWrapper>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <FieldLabel
            htmlFor={field.id}
            label={label}
            isRequired={isRequired}
            className="cursor-pointer font-normal peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          />
          {description != null && (
            <Typography variant="smallText" className="text-muted-foreground">
              {description}
            </Typography>
          )}
        </div>

        <Switch
          id={field.id}
          checked={checked}
          onCheckedChange={(c) => onChange(c === true)}
          onBlur={onBlur}
          disabled={isReadOnly}
          aria-invalid={errors.length > 0}
          aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
        />
      </div>

      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundFileInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const stringValue = value == null ? "" : String(value);
  const fileConfig = field.fileConfig;
  const acceptAttr = fileConfig?.accept?.join(",");

  const selectedNames = stringValue
    ? stringValue
        .split(";")
        .map((s) => s.split("|")[0])
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={field.id} label={label} isRequired={isRequired} />
      <Input
        id={field.id}
        type="file"
        accept={acceptAttr}
        multiple={fileConfig?.multiple}
        disabled={isReadOnly}
        onBlur={onBlur}
        onChange={(e) => {
          const files = e.target.files;
          if (!files || files.length === 0) {
            onChange(null);
            return;
          }
          if (fileConfig?.multiple) {
            const names = [...files].map((f) => `${f.name}|${f.size}`).join(";");
            onChange(names);
          } else {
            const f = files.item(0);
            onChange(f ? `${f.name}|${f.size}` : null);
          }
        }}
        aria-invalid={errors.length > 0}
        aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
        className={cn(errors.length > 0 && "border-destructive")}
      />
      {selectedNames && (
        <Typography variant="smallText" className="text-muted-foreground">
          Selected: {selectedNames}
        </Typography>
      )}
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundTextareaInput({
  field,
  value,
  onChange,
  onBlur,
  errors,
  isRequired,
  isVisible,
  isReadOnly,
  label,
}: FieldInputProps) {
  if (!isVisible) return null;

  const stringValue = value == null ? "" : String(value);
  const placeholder = typeof field.placeholder === "string" ? field.placeholder : undefined;

  return (
    <FieldWrapper>
      <FieldLabel htmlFor={field.id} label={label} isRequired={isRequired} />
      <textarea
        id={field.id}
        value={stringValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        readOnly={isReadOnly}
        disabled={isReadOnly}
        rows={4}
        aria-invalid={errors.length > 0}
        aria-describedby={errors.length > 0 ? `${field.id}-errors` : undefined}
        className={cn(
          "flex w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          errors.length > 0 && "border-destructive",
        )}
      />
      <FieldError fieldId={field.id} errors={errors} />
    </FieldWrapper>
  );
}

function PlaygroundHiddenInput({ field, value, onChange }: FieldInputProps) {
  useEffect(() => {
    if (value == null && field.defaultValue != null) {
      onChange(field.defaultValue);
    }
  }, [value, field.defaultValue, onChange]);

  return <input type="hidden" name={field.id} value={value == null ? "" : String(value)} />;
}

function PlaygroundComputed({ field, value, isVisible }: FieldComputedProps) {
  if (!isVisible) return null;

  let displayValue: string;
  if (value == null) displayValue = "";
  else if (typeof value === "boolean") displayValue = value ? "Yes" : "No";
  else if (typeof value === "object") displayValue = JSON.stringify(value);
  else displayValue = String(value);

  const computedLabel =
    field.label == null
      ? null
      : typeof field.label === "object" && "default" in field.label
        ? field.label.default
        : String(field.label);

  return (
    <div className="flex flex-col gap-1 py-3">
      {computedLabel != null && (
        <Typography variant="text" className="text-sm text-muted-foreground">
          {computedLabel}
        </Typography>
      )}
      <Typography variant="text" className="text-sm font-medium">
        {displayValue}
      </Typography>
    </div>
  );
}

export const playgroundFieldComponents = {
  text: PlaygroundTextInput,
  number: PlaygroundNumberInput,
  email: PlaygroundEmailInput,
  date: PlaygroundDateInput,
  select: PlaygroundSelectInput,
  checkbox: PlaygroundCheckboxInput,
  toggle: PlaygroundToggleInput,
  radio: PlaygroundRadioInput,
  file: PlaygroundFileInput,
  textarea: PlaygroundTextareaInput,
  hidden: PlaygroundHiddenInput,
  computed: PlaygroundComputed,
};
